import {Suspense, useEffect, useMemo, useRef} from 'react'
import {ErrorBoundary} from '@github-ui/react-core/error-boundary'
import {debounce} from '@github/mini-throttle'
import {announce} from '@github-ui/aria-live'
import {isLoggedIn} from '@github-ui/client-env'
import {CommentsSkeleton} from './CommentsSkeleton'
import {IssueCommentItem} from './IssueCommentItem'
import {ReviewSummaryItem} from './ReviewSummaryItem'
import {CommentsErrorFallback} from './CommentsErrorFallback'
import type {ConversationItem} from './utils/build-conversation-items'
import {ThreadPreview} from './ThreadPreview'
import {HydrateThreadPositioning} from './HydrateThreadPositioning'
import {useLoadAllIssueCommentsAndReviews} from './use-load-all-issue-comments-and-reviews'
import {isFeatureEnabled} from '@github-ui/feature-flags'
import {usePullRequestBasePath} from '../../stores/layout-selectors'
import {useInfiniteConversationCommentsQuery} from '../../page-data/loaders/use-conversation-comments'

export interface IssueCommentsContentProps {
  pullRequestAuthorLogin: string
  filteredConversationItems: ConversationItem[]
  onQuoteReply: (quotedText: string) => void
  onNavigateToDiffComment?: () => void
}

/**
 * Data loading container that fetches issue comments and review summaries.
 * Automatically fetches all available pages on mount when the server returns
 * a Link header with rel="next", up to the configured pagination limit.
 */
export function IssueCommentsContent(props: IssueCommentsContentProps) {
  return isFeatureEnabled('prs_new_conversation_comments_api') ? (
    <NewIssueCommentsContent {...props} />
  ) : (
    <LegacyIssueCommentsContent {...props} />
  )
}

function NewIssueCommentsContent(props: IssueCommentsContentProps) {
  const pathName = usePullRequestBasePath()
  const {isFetchingNextPage, hasNextPage} = useInfiniteConversationCommentsQuery({pathName, enabled: false})
  return <IssueCommentsComponent {...props} isLoadingMore={isFetchingNextPage || hasNextPage} />
}

function LegacyIssueCommentsContent(props: IssueCommentsContentProps) {
  const pathName = usePullRequestBasePath()
  const isLoadingMore = useLoadAllIssueCommentsAndReviews(pathName)
  return <IssueCommentsComponent {...props} isLoadingMore={isLoadingMore} />
}

interface IssueCommentsComponentProps {
  filteredConversationItems: ConversationItem[]
  isLoadingMore: boolean
  pullRequestAuthorLogin: string
  onQuoteReply: (quotedText: string) => void
  onNavigateToDiffComment?: () => void
}

/**
 * Presentational component that renders issue comments and review summaries.
 * Handles the display logic for comments, loading states, and zero states.
 */
function IssueCommentsComponent({
  filteredConversationItems,
  isLoadingMore,
  pullRequestAuthorLogin,
  onQuoteReply,
  onNavigateToDiffComment,
}: IssueCommentsComponentProps) {
  const canReact = isLoggedIn()
  const announceRef = useRef<HTMLDivElement>(null)
  const debouncedAnnounce = useMemo(() => debounce(announce, 300), [])

  useEffect(() => {
    // Only announce when we've finished loading all comments
    if (isLoadingMore) return

    const announceText =
      filteredConversationItems.length > 0
        ? `${filteredConversationItems.length} ${filteredConversationItems.length === 1 ? 'comment' : 'comments'}`
        : `No comments found`

    debouncedAnnounce(announceText, {element: announceRef.current as HTMLElement})
  }, [isLoadingMore, debouncedAnnounce, filteredConversationItems])

  const unassignedThreads = useMemo(
    () => filteredConversationItems.filter(item => item.type === 'thread').map(item => item.data),
    [filteredConversationItems],
  )

  return (
    <>
      {filteredConversationItems.length > 0 ? (
        <ErrorBoundary boundaryName="IssueCommentsContent" fallback={<CommentsErrorFallback />}>
          <Suspense fallback={<CommentsSkeleton reviewCommentsMode={false} />}>
            <HydrateThreadPositioning threads={unassignedThreads} />
            <div className="d-flex flex-column position-relative width-full gap-3">
              {filteredConversationItems.map(item =>
                // TODO: limit/page the rendered items so that we don't try to hydrate thread positioning for every
                // thread at once
                item.type === 'review-summary' ? (
                  <ReviewSummaryItem
                    key={item.key}
                    reviewSummary={item.data.summary}
                    reviewThreads={item.data.threads}
                    pullRequestAuthorLogin={pullRequestAuthorLogin}
                    canReact={canReact}
                    onQuoteReply={onQuoteReply}
                    withReviewStateIcon
                    onNavigateToDiffComment={onNavigateToDiffComment}
                  />
                ) : item.type === 'thread' ? (
                  <ThreadPreview
                    key={item.key}
                    thread={item.data}
                    selectedReviewId={null}
                    onNavigateToDiffComment={onNavigateToDiffComment}
                    headingLevel="h2"
                  />
                ) : (
                  <IssueCommentItem
                    key={item.key}
                    comment={item.data}
                    pullRequestAuthorLogin={pullRequestAuthorLogin}
                    canReact={canReact}
                    onQuoteReply={onQuoteReply}
                  />
                ),
              )}
              {isLoadingMore && <CommentsSkeleton reviewCommentsMode={false} />}
            </div>
          </Suspense>
        </ErrorBoundary>
      ) : isLoadingMore ? (
        <CommentsSkeleton reviewCommentsMode={false} />
      ) : null}
      <div className="sr-only" aria-live="polite" aria-atomic="true" ref={announceRef} />
    </>
  )
}
